import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { HUD } from '../ui/hud.js';

// Crumb & Comfort — downtown's furniture showroom. Explorable for now: sofas,
// a bed, a dining set and lamps on display. Buying furniture hooks into the
// house once it's built. Walk up to a piece for flavor; exit to downtown.

export function createFurnitureScene(){
  return () => new FurnitureScene();
}

class FurnitureScene {
  constructor(){
    this.poseT = 0;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xDCE6DA);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 10;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.46;

    this._buildRoom();
    this._buildShowroom();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: new THREE.Vector3(-4, 0, -2.6), r: 2.0, label: 'try the sofa', say: 'comfy! Toast sinks right in. (buying — coming with your house)' },
      { pos: new THREE.Vector3(4, 0, -2.6), r: 2.0, label: 'test the bed', say: 'so plush. Toast could nap here. new furniture soon!' },
      { pos: new THREE.Vector3(0, 0, -3.6), r: 2.0, label: 'admire the dining set', say: 'a lovely little dining set. it\'ll furnish your house later.' },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'furniture' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.85));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.5);
    key.position.set(4, 10, 6); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: 0xBE9A6E }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), new THREE.MeshLambertMaterial({ color: 0x9CBE93 }));
    back.position.set(0, 4, -6); this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    const marquee = textSprite('CRUMB & COMFORT', 28, '#FFF4DF', 4.6, 1.1);
    marquee.position.set(0, 3.4, -5.7); this.scene.add(marquee);
    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to downtown', 24, '#3A2A1C', 2.8, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildShowroom(){
    // sofa (left)
    const sofa = new THREE.Group(); sofa.position.set(-4, 0, -3); this.scene.add(sofa);
    box(2.2, 0.5, 1.0, C.raspberry, 0, 0.4, 0, sofa);            // seat
    box(2.2, 0.7, 0.25, C.raspberry, 0, 0.85, -0.4, sofa);      // back
    box(0.25, 0.6, 1.0, C.raspberry, -1.0, 0.6, 0, sofa);       // arms
    box(0.25, 0.6, 1.0, C.raspberry, 1.0, 0.6, 0, sofa);
    box(0.6, 0.4, 0.6, 0x2E6B45, 1.7, 0.2, 0.4, sofa);          // side table + plant
    sphere(0.35, C.sprout, 1.7, 0.7, 0.4, sofa, 10);

    // bed (right)
    const bed = new THREE.Group(); bed.position.set(4, 0, -3); this.scene.add(bed);
    box(2.0, 0.4, 2.4, C.rye, 0, 0.35, 0, bed);                 // frame
    box(1.9, 0.25, 2.2, C.cream, 0, 0.62, 0, bed);             // mattress
    box(1.9, 0.15, 0.6, C.cobalt, 0, 0.75, -0.8, bed);         // blanket fold
    box(0.7, 0.2, 0.35, C.white, -0.5, 0.78, -0.9, bed);      // pillow
    box(0.7, 0.2, 0.35, C.white, 0.5, 0.78, -0.9, bed);
    box(2.0, 1.0, 0.2, C.crustDark, 0, 0.9, -1.2, bed);        // headboard

    // dining set (center-back)
    const din = new THREE.Group(); din.position.set(0, 0, -3.6); this.scene.add(din);
    cyl(0.5, 0.5, 0.1, C.crust, 0, 0.9, 0, din, 16);
    cyl(0.08, 0.08, 0.9, C.crustDark, 0, 0.45, 0, din, 8);
    [[0.8, 0], [-0.8, 0], [0, 0.8], [0, -0.8]].forEach(([x, z]) => {
      box(0.4, 0.5, 0.4, C.tangerine, x, 0.25, z, din);
      box(0.4, 0.5, 0.1, C.tangerine, x, 0.7, z + (z <= 0 ? -0.15 : 0.15), din);
    });

    // floor lamps + a display rug
    const lamp = (x, z) => {
      cyl(0.2, 0.24, 0.08, C.crustDark, x, 0.05, z, this.scene, 12);
      cyl(0.04, 0.04, 1.8, C.crustDark, x, 0.9, z, this.scene, 8);
      const shade = cyl(0.35, 0.22, 0.4, 0xFFE9A8, x, 1.9, z, this.scene, 14);
      shade.material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 });
    };
    lamp(-5.6, 1.5); lamp(5.6, 1.5);
    const rug = new THREE.Mesh(new THREE.CircleGeometry(1.8, 32), new THREE.MeshLambertMaterial({ color: C.cobalt }));
    rug.rotation.x = -Math.PI/2; rug.position.set(0, 0.02, 1.5); this.scene.add(rug);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('crumb & comfort');
    HUD.say('Crumb & Comfort! Test the sofas and beds — furniture for your house is coming soon. Exit to downtown.');
    HUD.setHint('click to walk · walk up to a piece or the exit');
    HUD.hideHintSoon(7000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); }

  _tap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-2.4, Math.min(5.4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    if (this.poseT > 0){
      this.poseT -= dt;
      if (this.poseT <= 0){ this.bread.resetLimbs(); this.bread.drawFace('happy'); }
    }

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => {
          if (near.action) near.action();
          else { HUD.say(near.say); this.bread.drawFace('yum'); this.poseT = 1.4; }
        });
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
